<template>
  <div class="token-topup-stepper">
    <p class="text-muted small mb-3">
      Выберите количество токенов (кратно {{ step }}). Чем больше объём — тем ниже цена за токен.
    </p>
    <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="disabled || tokens <= min"
        aria-label="Уменьшить на 100"
        @click="bump(-step)"
      >
        −
      </button>
      <input
        v-model.number="tokensInput"
        type="number"
        class="form-control text-center token-topup-stepper__input"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        @blur="normalizeInput"
      />
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="disabled || tokens >= max"
        aria-label="Увеличить на 100"
        @click="bump(step)"
      >
        +
      </button>
      <span class="text-muted small">токенов</span>
    </div>
    <div v-if="validationError" class="alert alert-danger small py-2 mb-2">{{ validationError }}</div>
    <div v-else class="mb-3">
      <div class="fw-semibold">
        К оплате:
        <span v-if="quoteLoading" class="text-muted">расчёт…</span>
        <span v-else>{{ money(displayPriceRub) }}</span>
      </div>
      <div v-if="displayRubPerToken != null" class="small text-muted">
        ≈ {{ displayRubPerToken.toFixed(2).replace('.', ',') }} ₽ за токен
      </div>
    </div>
    <button
      type="button"
      class="btn btn-primary"
      :disabled="disabled || ordering || !!validationError || quoteLoading"
      @click="$emit('order', tokens)"
    >
      <span v-if="ordering" class="spinner-border spinner-border-sm me-1" />
      Оплатить
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import api from '@/api'
import {
  TOPUP_DEFAULT_AMOUNT,
  TOPUP_MAX_TOKENS,
  TOPUP_MIN_TOKENS,
  TOPUP_STEP,
  normalizeTopupTokens,
  topupQuotePreview,
} from '@/billing/topupPricing'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  ordering: { type: Boolean, default: false },
})

defineEmits(['order'])

const min = TOPUP_MIN_TOKENS
const max = TOPUP_MAX_TOKENS
const step = TOPUP_STEP

const tokensInput = ref(TOPUP_DEFAULT_AMOUNT)
const quoteFromServer = ref(null)
const quoteLoading = ref(false)
let quoteTimer = null

const tokens = computed(() => {
  const { tokens: t } = normalizeTopupTokens(tokensInput.value)
  return t ?? TOPUP_DEFAULT_AMOUNT
})

const validationError = computed(() => normalizeTopupTokens(tokensInput.value).error)

const localPreview = computed(() => topupQuotePreview(tokens.value))

const displayPriceRub = computed(() => {
  if (quoteFromServer.value?.price_rub != null) {
    return Number(quoteFromServer.value.price_rub)
  }
  return localPreview.value?.price_rub ?? 0
})

const displayRubPerToken = computed(() => {
  if (quoteFromServer.value?.rub_per_token != null) {
    return Number(quoteFromServer.value.rub_per_token)
  }
  return localPreview.value?.rub_per_token ?? null
})

function money(n) {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  )
}

function bump(delta) {
  const next = Math.min(max, Math.max(min, tokens.value + delta))
  tokensInput.value = next
}

function normalizeInput() {
  const { tokens: t, error } = normalizeTopupTokens(tokensInput.value)
  if (!error && t != null) tokensInput.value = t
}

async function fetchQuote() {
  if (validationError.value) {
    quoteFromServer.value = null
    return
  }
  quoteLoading.value = true
  try {
    const { data } = await api.get('/billing/topup/quote/', { params: { tokens: tokens.value } })
    quoteFromServer.value = data
  } catch {
    quoteFromServer.value = null
  } finally {
    quoteLoading.value = false
  }
}

function scheduleQuote() {
  clearTimeout(quoteTimer)
  quoteTimer = setTimeout(fetchQuote, 200)
}

watch(tokens, scheduleQuote, { immediate: true })
</script>

<style scoped>
.token-topup-stepper__input {
  width: 7rem;
  max-width: 100%;
}
</style>
